<template>
  <HeaderBar />

  <section class="carvia-container">
    <div class="flex flex-col">
      <div class="flex flex-col gap-4">
        <h2>{{ t('pricingModifiers') }}</h2>
        <p>{{ t('pricingModifiersDescription') }}</p>
        <CVButton :to="{ name: 'editPricingModifiers' }">{{ t('editPricingModifiers') }}</CVButton>
      </div>
      <Divider />
      <div class="flex flex-col gap-4">
        <h2>{{ t('extras') }}</h2>
        <CVButton :to="{ name: 'editExtras' }">{{ t('editExtras') }}</CVButton>
      </div>
      <Divider />
      <div class="flex flex-col gap-4">
        <h2>{{ t('kmRelatedStuff') }}</h2>
        <CVButton :to="{ name: 'editYieldManagementKm' }">{{ t('editKmRelatedStuff') }}</CVButton>
      </div>
      <Divider />
      <div class="flex flex-col gap-4">
        <h2>{{ t('areaCharges') }}</h2>
        <p>{{ t('areaChargesDescription') }}</p>
        <CVButton :to="{ name: 'areaCharges' }">{{ t('editAreaCharges') }}</CVButton>
      </div>
      <Divider />
      <div class="flex flex-col gap-4">
        <h2>{{ t('dunning') }}</h2>
        <p>{{ t('dunningDescription') }}</p>
        <CVButton :to="{ name: 'editYieldManagementDunning' }">{{ t('editDunning') }}</CVButton>
      </div>
      <Divider />
      <div class="flex flex-col gap-4">
        <h2>{{ t('otherSettings') }}</h2>
        <p>{{ t('otherSettingsDescription') }}</p>
        <CVButton :to="{ name: 'editYieldManagementOther' }">{{ t('editOtherSettings') }}</CVButton>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useI18n } from 'vue-i18n';
import { useUiStore } from '@/stores/ui.store.ts';
import Divider from '@/components/Divider.vue';
import { watchEffect } from 'vue';
import { useMoneyClicks } from '@/hooks/use-money-clicks.ts';

const { t } = useI18n();
const uiStore = useUiStore();

watchEffect(() => {
  uiStore.setHeaderTitle(t('yieldManagement'));
});

useMoneyClicks();
</script>

<i18n lang="json">
{
  "en": {
    "yieldManagement": "Yield management 💸",
    "pricingModifiers": "Pricing modifiers",
    "pricingModifiersDescription": "Multipliers that are applied to the base price if certain conditions are met.",
    "editPricingModifiers": "Edit pricing modifiers",
    "extras": "Extras",
    "editExtras": "Edit extras",
    "kmRelatedStuff": "Km related stuff",
    "editKmRelatedStuff": "Edit km related stuff",
    "areaCharges": "Area charges",
    "areaChargesDescription": "Create named prices and draw areas. A transfer is charged when it enters or leaves an area.",
    "editAreaCharges": "Edit area charges",
    "dunning": "Dunning",
    "dunningDescription": "Default payment term for customers.",
    "editDunning": "Edit dunning",
    "otherSettings": "Other settings",
    "editOtherSettings": "Edit other settings",
    "otherSettingsDescription": "Includes deposits and young driver age."
  },
  "de": {
    "yieldManagement": "Yield Management 💸",
    "pricingModifiers": "Preismodifikatoren",
    "pricingModifiersDescription": "Multiplikatoren, die auf den Basispreis angewendet werden, wenn bestimmte Bedingungen erfüllt sind.",
    "editPricingModifiers": "Preismodifikatoren bearbeiten",
    "extras": "Extras",
    "editExtras": "Extras bearbeiten",
    "kmRelatedStuff": "KM-bezogene Sachen",
    "editKmRelatedStuff": "KM-bezogene Sachen bearbeiten",
    "areaCharges": "Gebietsaufschläge",
    "areaChargesDescription": "Preise anlegen und Gebiete einzeichnen. Der Aufschlag gilt, wenn ein Transfer in ein Gebiet hinein- oder herausfährt.",
    "editAreaCharges": "Gebietsaufschläge bearbeiten",
    "dunning": "Mahnwesen",
    "dunningDescription": "Standard-Zahlungsziel für Kunden.",
    "editDunning": "Mahnwesen bearbeiten",
    "otherSettings": "Sonstige Einstellungen",
    "editOtherSettings": "Sonstige Einstellungen bearbeiten",
    "otherSettingsDescription": "Enthält Kautionen und Jungfahreralter."
  },
  "es": {
    "yieldManagement": "Gestión de yield 💸",
    "pricingModifiers": "Modificadores de precio",
    "pricingModifiersDescription": "Multiplicadores que se aplican al precio base cuando se cumplen determinadas condiciones.",
    "editPricingModifiers": "Editar modificadores de precio",
    "extras": "Extras",
    "editExtras": "Editar extras",
    "kmRelatedStuff": "Opciones relacionadas con km",
    "editKmRelatedStuff": "Editar opciones de km",
    "areaCharges": "Recargos por zona",
    "areaChargesDescription": "Crea precios con nombre y dibuja zonas. El recargo se aplica cuando un traslado entra o sale de una zona.",
    "editAreaCharges": "Editar recargos por zona",
    "dunning": "Gestión de cobros",
    "dunningDescription": "Plazo de pago predeterminado para clientes.",
    "editDunning": "Editar gestión de cobros",
    "otherSettings": "Otros ajustes",
    "editOtherSettings": "Editar otros ajustes",
    "otherSettingsDescription": "Incluye depósitos y edad mínima para conductores jóvenes."
  }
}
</i18n>
